import { useState, useRef } from 'react'
import './Contact.css'

const API_ENDPOINT = 'https://forms.2merative.com/api/jesse-contact.php'

const SERVICES = [
  'Custom Tattoo',
  'Tattoo Therapy',
  'Touch-Up / Rework',
  'Cover-Up',
  'Black & Grey',
  'Colour Tattoo',
  'Not sure yet — I want to chat',
]

const INITIAL_STATE = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  referenceImage: null,
}

function FieldError({ message }) {
  return message ? (
    <span className="contact__field-error" role="alert" aria-live="polite">
      {message}
    </span>
  ) : null
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL_STATE)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle')
  const [serverMessage, setServerMessage] = useState('')
  const fileRef = useRef(null)

  const validate = () => {
    const newErrors = {}

    if (!form.name.trim()) newErrors.name = 'Name is required.'

    if (!form.email.trim()) {
      newErrors.email = 'Email is required.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.'
    }

    if (!form.service) newErrors.service = 'Please select a service.'

    if (!form.message.trim()) {
      newErrors.message = 'Please tell us a bit about your tattoo idea.'
    } else if (form.message.trim().length < 20) {
      newErrors.message = 'Please add a bit more detail (20 characters min).'
    }

    if (form.referenceImage && form.referenceImage.size > 10 * 1024 * 1024) {
      newErrors.referenceImage = 'Image must be under 10MB.'
    }

    return newErrors
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target

    if (name === 'referenceImage') {
      setForm((prev) => ({ ...prev, referenceImage: files?.[0] || null }))
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    if (status === 'error') {
      setStatus('idle')
      setServerMessage('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const newErrors = validate()

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setServerMessage('')
      const firstErrorKey = Object.keys(newErrors)[0]
      document.getElementById(`field-${firstErrorKey}`)?.focus()
      return
    }

    setStatus('submitting')
    setServerMessage('')

    try {
      const data = new FormData()
      data.append('name', form.name)
      data.append('email', form.email)
      data.append('phone', form.phone)
      data.append('service', form.service)
      data.append('message', form.message)

      if (form.referenceImage) {
        data.append('referenceImage', form.referenceImage)
      }

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        body: data,
      })

      const rawText = await response.text()

      let result = null
      try {
        result = JSON.parse(rawText)
      } catch {
        result = null
      }

      if (response.ok && (result?.success || result?.ok)) {
        setStatus('success')
        setServerMessage('')
        setErrors({})
        setForm(INITIAL_STATE)
        if (fileRef.current) fileRef.current.value = ''
        return
      }

      setStatus('error')
      setServerMessage(
        result?.error ||
          result?.message ||
          rawText ||
          `Request failed with status ${response.status}.`
      )
    } catch (error) {
      setStatus('error')
      setServerMessage(error?.message || 'Network error. Please try again.')
    }
  }

  return (
    <section className="contact section" aria-labelledby="contact-heading">
      <div className="container">
        <div className="contact__inner">
          <aside className="contact__info">
            <span className="contact__eyebrow">Let's Talk Ink</span>
            <h2 className="contact__heading" id="contact-heading">
              Get In
              <span className="contact__heading-script"> Touch</span>
            </h2>
            <p className="contact__body">
              Ready to start something? Have a question? Want to enquire about Tattoo
              Therapy? Drop a message and Jesse will get back to you within 2 business days.
            </p>

            <div className="contact__details">
              <div className="contact__detail-item">
                <span className="contact__detail-icon" aria-hidden="true">📧</span>
                <div>
                  <strong>Email</strong>
                  <a href="mailto:admin@jessebtattoo.com">admin@jessebtattoo.com</a>
                </div>
              </div>

              <div className="contact__detail-item">
                <span className="contact__detail-icon" aria-hidden="true">📍</span>
                <div>
                  <strong>Studio</strong>
                  <span>1/5 Canberra Dr, Aberfoyle Park, Adelaide, SA 5159</span>
                </div>
              </div>

              <div className="contact__detail-item">
                <span className="contact__detail-icon" aria-hidden="true">📞</span>
                <div>
                  <strong>Phone</strong>
                  <a href="tel:0430737400">0430 737 400</a>
                </div>
              </div>

              <div className="contact__detail-item">
                <span className="contact__detail-icon" aria-hidden="true">🕐</span>
                <div>
                  <strong>Response Time</strong>
                  <span>Within 2 business days</span>
                </div>
              </div>
            </div>
          </aside>

          <div className="contact__form-wrap">
            {status === 'success' ? (
              <div className="contact__success" role="alert">
                <span className="contact__success-icon" aria-hidden="true">✦</span>
                <h3>Message sent!</h3>
                <p>
                  Thanks for reaching out. Jesse will be in touch within 2 business
                  days. Keep an eye on your inbox.
                </p>
                <button
                  type="button"
                  className="contact__reset-btn"
                  onClick={() => {
                    setStatus('idle')
                    setServerMessage('')
                  }}
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form
                className="contact__form"
                onSubmit={handleSubmit}
                noValidate
                aria-label="Tattoo enquiry form"
                encType="multipart/form-data"
              >
                <div className="contact__field">
                  <label htmlFor="field-name" className="contact__label">
                    Full Name <span aria-hidden="true">*</span>
                  </label>
                  <input
                    type="text"
                    id="field-name"
                    name="name"
                    className={`contact__input${errors.name ? ' contact__input--error' : ''}`}
                    value={form.name}
                    onChange={handleChange}
                    autoComplete="name"
                    placeholder="Your full name"
                  />
                  <FieldError message={errors.name} />
                </div>

                <div className="contact__field-row">
                  <div className="contact__field">
                    <label htmlFor="field-email" className="contact__label">
                      Email <span aria-hidden="true">*</span>
                    </label>
                    <input
                      type="email"
                      id="field-email"
                      name="email"
                      className={`contact__input${errors.email ? ' contact__input--error' : ''}`}
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      placeholder="your@email.com"
                    />
                    <FieldError message={errors.email} />
                  </div>

                  <div className="contact__field">
                    <label htmlFor="field-phone" className="contact__label">
                      Phone <span className="contact__optional">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      id="field-phone"
                      name="phone"
                      className="contact__input"
                      value={form.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="04xx xxx xxx"
                    />
                  </div>
                </div>

                <div className="contact__field">
                  <label htmlFor="field-service" className="contact__label">
                    Preferred Service <span aria-hidden="true">*</span>
                  </label>
                  <select
                    id="field-service"
                    name="service"
                    className={`contact__select${errors.service ? ' contact__input--error' : ''}`}
                    value={form.service}
                    onChange={handleChange}
                  >
                    <option value="">— Select a service —</option>
                    {SERVICES.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                  <FieldError message={errors.service} />
                </div>

                <div className="contact__field">
                  <label htmlFor="field-message" className="contact__label">
                    Tell Me About Your Idea <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="field-message"
                    name="message"
                    className={`contact__textarea${errors.message ? ' contact__input--error' : ''}`}
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Describe your tattoo idea, placement, size, style, any meaningful context — the more detail the better!"
                  />
                  <FieldError message={errors.message} />
                </div>

                <div className="contact__field">
                  <label htmlFor="field-referenceImage" className="contact__label">
                    Reference Image <span className="contact__optional">(optional)</span>
                  </label>
                  <input
                    type="file"
                    id="field-referenceImage"
                    name="referenceImage"
                    className="contact__file"
                    accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.pdf"
                    onChange={handleChange}
                    ref={fileRef}
                  />
                  {form.referenceImage && (
                    <p className="contact__file-selected">
                      Selected: {form.referenceImage.name}
                    </p>
                  )}
                  <FieldError message={errors.referenceImage} />
                </div>

                {status === 'error' && (
                  <div className="contact__error-banner" role="alert">
                    {serverMessage ? (
                      <>
                        <strong>Send failed:</strong> {serverMessage}
                      </>
                    ) : (
                      <>
                        Something went wrong. Please try again or email{' '}
                        <a href="mailto:Jessebtattoo@outlook.com">
                          Jessebtattoo@outlook.com
                        </a>.
                      </>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="contact__submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Sending…' : 'Send Enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}